import { useState, useId } from "react";
import { Lock, KeyRound } from "lucide-react";
import { AppMark } from "../components/AppMark";
import { FirmaGobernadora } from "../components/FirmaGobernadora";
import { Version } from "../components/Version";
import { DescargaApp } from "../components/DescargaApp";
import { loginCognito, veniaDeExpirar, type CognitoLoginResult } from "../lib/auth";
import { PROGRAMA } from "../config";
import type { Session } from "../types";
import { confirmPasswordReset, requestPasswordReset } from "../lib/cognito";
import { useTitulo } from "../lib/useTitulo";

export function Login({ onLogin }: { onLogin: (s: Session) => void }) {
  // Une cada <label> con su campo (WCAG 1.3.1): sin esto el lector anunciaba
  // «cuadro de texto» a secas y un clic en la etiqueta no enfocaba nada.
  const uid = useId();
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  // Se lee UNA vez al montar (la función borra la marca): si no, cada re-render
  // la consumiría y el aviso parpadearía y desaparecería.
  const [expirada] = useState(veniaDeExpirar);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Reto de primer login: las cuentas creadas por admin traen contraseña temporal
  // y Cognito obliga a definir una nueva antes de emitir tokens.
  const [pending, setPending] = useState<
    Extract<CognitoLoginResult, { kind: "newPasswordRequired" }> | null
  >(null);
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [resetStep, setResetStep] = useState<"none" | "code" | "done">("none");
  // true cuando el código lo pidió el personal (restablecer desde el panel) y no
  // la persona con «Olvidé mi contraseña»: cambia solo el texto del aviso.
  const [resetPorPersonal, setResetPorPersonal] = useState(false);
  const [resetCode, setResetCode] = useState("");
  useTitulo(pending ? "Define tu contraseña" : resetStep === "code" ? "Recupera tu contraseña" : "Iniciar sesión");

  // Cognito devuelve mensajes en inglés y a veces técnicos; se traducen a algo útil.
  function errMsg(e: unknown): string {
    const m = String((e as any)?.message ?? "");
    if (/disabled/i.test(m)) return "Esta cuenta está suspendida. Habla con la bibliotecaria.";
    if (/temporary password has expired/i.test(m)) {
      return "La contraseña temporal venció. Pide a la bibliotecaria que te reenvíe la invitación.";
    }
    if (/invalid session/i.test(m)) {
      return "Pasó demasiado tiempo. Vuelve a entrar con tu contraseña temporal.";
    }
    if (/code mismatch|invalid verification code/i.test(m)) return "El código no es correcto. Revísalo e inténtalo otra vez.";
    if (/expired code|code.*expired/i.test(m)) return "El código venció. Pulsa «Olvidé mi contraseña» para recibir uno nuevo.";
    if (/limit exceeded|too many|attempt limit/i.test(m)) return "Demasiados intentos. Espera unos minutos y vuelve a intentarlo.";
    if (/no registered\/verified email|cannot reset password/i.test(m)) {
      return "Esta cuenta no tiene un correo verificado. La bibliotecaria puede ayudarte a entrar.";
    }
    if (/incorrect|not authoriz/i.test(m)) return "Usuario o contraseña incorrectos.";
    if (/user does not exist/i.test(m)) return "Esa cuenta no existe.";
    if (/password/i.test(m) && /(policy|length|constraint|long|satisfy)/i.test(m)) {
      return "La contraseña debe tener 10 caracteres o más, con mayúscula, minúscula y número.";
    }
    return m || "No se pudo iniciar sesión.";
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (resetStep === "code") return submitReset();
    setError("");
    setBusy(true);
    try {
      const r = await loginCognito(user, password);
      if (r.kind === "ok") onLogin(r.session);
      else if (r.kind === "resetRequired") {
        // Un código nuevo: el que mandó el panel pudo haber vencido (dura 1 h).
        await requestPasswordReset(user.trim());
        setResetPorPersonal(true);
        setPassword("");
        setResetStep("code");
      } else setPending(r);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function startReset() {
    setError("");
    if (!user.trim()) { setError("Escribe primero el correo de tu cuenta."); return; }
    setBusy(true);
    try {
      await requestPasswordReset(user.trim());
      setResetPorPersonal(false);
      setResetStep("code");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitReset() {
    setError("");
    if (!resetCode.trim()) { setError("Escribe el código que recibiste por correo."); return; }
    if (newPass.length < 10) { setError("La contraseña debe tener al menos 10 caracteres."); return; }
    if (newPass !== newPass2) { setError("Las contraseñas no coinciden."); return; }
    setBusy(true);
    try {
      await confirmPasswordReset(user.trim(), resetCode.trim(), newPass);
      setResetStep("done");
      setPassword(""); setResetCode(""); setNewPass(""); setNewPass2("");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitNewPassword(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (newPass.length < 10) {
      setError("La contraseña debe tener al menos 10 caracteres.");
      return;
    }
    if (newPass !== newPass2) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!pending) return;
    setBusy(true);
    try {
      const r = await pending.complete(newPass);
      if (r.kind === "ok") onLogin(r.session);
      else setError("Cognito pidió un paso adicional no soportado.");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  // --- Pantalla de definir contraseña (primer login de Cognito) ---
  if (pending) {
    return (
      <div className="login-wrap">
        <form className="login-card" onSubmit={submitNewPassword}>
          <div className="login-title">
            <div className="mark">
              <KeyRound aria-hidden size={30} />
            </div>
            <h1>Define tu contraseña</h1>
          </div>
          <div className="login-sub">Es tu primer ingreso. Elige una contraseña nueva.</div>

          <div className="field">
            <label htmlFor={`${uid}-nueva-contrasena`}>Nueva contraseña</label>
            <input
              id={`${uid}-nueva-contrasena`}
              type="password"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor={`${uid}-repite-la-contrasena`}>Repite la contraseña</label>
            <input
              id={`${uid}-repite-la-contrasena`}
              type="password"
              value={newPass2}
              onChange={(e) => setNewPass2(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          {error && <div className="error-text" role="alert">{error}</div>}

          <button className="btn-primary" type="submit" disabled={busy}>
            <Lock aria-hidden size={18} /> {busy ? "Guardando…" : "Guardar y entrar"}
          </button>
          {/* Salida: el reto caduca a los 3 minutos y sin esto había que recargar. */}
          <button type="button" className="btn" onClick={() => { setPending(null); setNewPass(""); setNewPass2(""); setError(""); }}>
            Volver al ingreso
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        {/*
          El logo de AVP va grande y primero: quien entra tiene que reconocer de un
          vistazo que esto es un servicio de la Administración de Vivienda Pública,
          no una web privada que le pide sus datos. En vivienda pública esa duda es
          real y frena a la gente.
        */}
        <div className="login-agencia">
          {/*
            Sin rótulo debajo: el propio logo YA lleva escrito «Administración de
            Vivienda Pública» y «Gobierno de Puerto Rico». Repetirlo en texto era
            decir tres veces lo mismo en los primeros 200 px de la tarjeta —el
            logo, el rótulo y el subtítulo—, y no añadía nada a quien no lo
            hubiera leído en el escudo.

            El `alt` se queda: para quien no ve la imagen, ese texto ES el logo.
          */}
          <img
            className="login-logo"
            src="/assets/avp-logo.png"
            alt={PROGRAMA.agency}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        </div>

        <div className="login-title">
          {/* El símbolo de KidsIQ a tamaño: es la marca de la app y el icono con el
              que aparece en las tiendas. No es donde se recortan píxeles. */}
          <AppMark size={76} />
          <h1>{PROGRAMA.program}</h1>
        </div>
        {expirada && (
          <div className="callout" style={{ borderColor: "var(--elemental)" }}>
            <strong>Tu sesión expiró</strong>
            <span>
              Por seguridad la sesión caduca tras un tiempo de inactividad. Vuelve a entrar;
              no se perdió nada de lo que guardaste.
            </span>
          </div>
        )}

        {/* Texto, no recuadro: los `.callout` se reservan para lo que hay que
            leer de verdad, como el aviso de sesión expirada de arriba. */}
        <p className="login-lema">{PROGRAMA.tagline}</p>

        <div className="field">
          <label htmlFor={`${uid}-usuario`}>Usuario</label>
          <input
            id={`${uid}-usuario`}
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="username"
            autoFocus
          />
        </div>
        {resetStep === "code" ? (
          <>
            <div className="callout">
              <strong>Revisa tu correo</strong>
              <span>
                {resetPorPersonal
                  ? "Tu contraseña fue restablecida. Te enviamos un código a tu correo: escríbelo aquí y elige tu nueva contraseña."
                  : "Escribe el código recibido y elige tu nueva contraseña."}
              </span>
            </div>
            <div className="field">
              <label htmlFor={`${uid}-codigo-de-recuperacion`}>Código de recuperación</label>
              <input id={`${uid}-codigo-de-recuperacion`} value={resetCode} onChange={(e) => setResetCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" autoFocus />
            </div>
            <div className="field">
              <label htmlFor={`${uid}-nueva-contrasena-2`}>Nueva contraseña</label>
              <input id={`${uid}-nueva-contrasena-2`} type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} autoComplete="new-password" />
            </div>
            <div className="field">
              <label htmlFor={`${uid}-repite-la-contrasena-2`}>Repite la contraseña</label>
              <input id={`${uid}-repite-la-contrasena-2`} type="password" value={newPass2} onChange={(e) => setNewPass2(e.target.value)} autoComplete="new-password" />
            </div>
          </>
        ) : (
          <div className="field">
            <label htmlFor={`${uid}-contrasena`}>Contraseña</label>
            <input
              id={`${uid}-contrasena`}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
        )}

        {resetStep === "done" && <div className="feedback ok" role="status">Contraseña actualizada. Ya puedes entrar.</div>}
        {error && <div className="error-text" role="alert">{error}</div>}

        <button className="btn-primary" type="submit" disabled={busy}>
          <Lock aria-hidden size={18} /> {busy ? "Procesando…" : resetStep === "code" ? "Guardar contraseña" : "Entrar"}
        </button>
        {resetStep === "code" ? (
          <button type="button" className="btn" onClick={() => { setResetStep("none"); setError(""); }}>Volver al ingreso</button>
        ) : (
          <button type="button" className="login-enlace" onClick={() => void startReset()} disabled={busy}>Olvidé mi contraseña</button>
        )}

        {/*
          Aquí iban los botones de "Acceso rápido (demo)", que hacían login con
          las contraseñas de `accounts.ts`. ELIMINADOS al ir a producción: eran
          credenciales reales servidas en el JavaScript de una página pública.
        */}

        <div className="login-hint">
          Entra con el correo y la contraseña de tu cuenta.
        </div>

        <DescargaApp />

        {/*
          Cierra la tarjeta. Arriba del todo está el logo de AVP —"esto es un
          servicio de la Administración de Vivienda Pública"— y aquí abajo el
          gobierno del que depende. Gobierno → agencia → programa: la jerarquía
          queda completa sin que nadie tenga que leer un párrafo.

          Va al final y no junto al formulario a propósito: es un aval, no un
          paso más para entrar, y no debe estorbar a quien viene a escribir su
          usuario y salir.
        */}
        {/* 120 y no 160: es un aval, y en la única pantalla que debería caber
            entera sin desplazar no puede llevarse 170 px. Sigue leyéndose. */}
        <FirmaGobernadora rotulo="Gobierno de Puerto Rico" ancho={120} />
        <Version />
      </form>
    </div>
  );
}
